import { gql, TypedDocumentNode } from "@apollo/client";
import {
  NewReference,
  CreateNewReferenceMutationMutation,
  CreateNewReferenceMutationMutationVariables,
  EditNewReferenceMutationMutation,
  EditNewReferenceMutationMutationVariables,
  EditReferenceQuery,
  EditReferenceQueryVariables,
  EditAndApproveNewReferenceMutationMutation,
  EditAndApproveNewReferenceMutationMutationVariables,
  DeleteNewReferenceMutationMutation,
  DeleteNewReferenceMutationMutationVariables,
  StarredReferenceQuery,
  StarredReferenceQueryVariables,
  AddStarredNewReferenceMutationMutation,
  AddStarredNewReferenceMutationMutationVariables,
  RemoveStarredNewReferenceMutationMutation,
  RemoveStarredNewReferenceMutationMutationVariables,
  AllProjectNewReferenceQueryQuery,
  AllProjectNewReferenceQueryQueryVariables,
  OnepagerReferenceQuery,
  OnepagerReferenceQueryVariables,
  FilteredProjectReferencesQueryVariables,
  FilteredProjectReferencesQuery,
} from "generated/graphql";

export const newReferenceShortFragment: TypedDocumentNode<NewReference> = gql`
  fragment NewReference_shortFields on NewReference {
    id
    employeeId
    projectId
    parentReferenceId
    from
    to
    roles {
      id
      referenceRoleId
      newText
      referenceRole {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    siaPhases
    hoaiPhases
    frPhases
    itPhases
    primaryPhase
    services
    pictureCredit
    picture {
      id
      url(width: 400, height: 107)
      urlWord: url(width: 1200, height: 321)
      urlFull: url(width: 0)
      focusX
      focusY
    }
    applicationAreas
    title {
      id
      de
      en
      fr
      it
    }
    description {
      id
      de
      en
      fr
      it
    }
    facts {
      id
      value {
        id
        de
        en
        fr
        it
      }
      newText
      keyId
      key {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    internalNote
    privateReferenceProjectClientName
    privateReferenceProjectName {
      id
      de
      en
      fr
      it
    }
    privateReferenceProjectDescription {
      id
      de
      en
      fr
      it
    }
    privateReferenceProjectInvestment
    privateReferenceProjectInvestmentCurrency
    privateReferenceProjectFrom
    privateReferenceProjectTo
    privateReferenceProjectSiaPhases
    privateReferenceProjectHoaiPhases
    privateReferenceProjectFrPhases
    privateReferenceProjectItPhases
    privateReferenceProjectPrimaryPhase
    privateReferenceProjectApplicationAreas
    privateReferenceProjectServices
    privateReferenceProjectCountry
    privateReferenceProjectFacts {
      id
      value {
        id
        de
        en
        fr
        it
      }
      newText
      keyId
      key {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
  }
`;

export const newReferenceFragment: TypedDocumentNode<NewReference> = gql`
  ${newReferenceShortFragment}
  fragment NewReference_fields on NewReference {
    ...NewReference_shortFields
    employee {
      id
      fullName
    }
    modifiedBy {
      __typename
    }
    approvedAt
    deletedAt
    insertedAt
    connectedFields {
      field
      isConnected
    }
    project {
      id
      abacusProjectId
      name {
        id
        de
        en
        fr
        it
      }
      client {
        id
        name
      }
      clientHidden
      investment
      investmentCurrency
      isUnlisted
      parentProjects {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
    childReferences {
      id
      title {
        id
        de
        en
        fr
        it
      }
      projectId
      employee {
        id
        fullName
      }
    }
    isReadyForReview
  }
`;

export const parentReferenceFragment: TypedDocumentNode<NewReference> = gql`
  fragment ParentReference_fields on NewReference {
    id
    applicationAreas
    siaPhases
    hoaiPhases
    frPhases
    itPhases
    primaryPhase
    services
    from
    to
    picture {
      id
      url
    }
    parentReference {
      id
      applicationAreas
      siaPhases
      hoaiPhases
      frPhases
      itPhases
      primaryPhase
      services
      from
      to
      title {
        id
        de
        en
        fr
        it
      }
      picture {
        id
        url
      }
    }
    title {
      id
      de
      en
      fr
      it
    }
  }
`;

export const projectReferenceFragment: TypedDocumentNode<NewReference> = gql`
  fragment ProjectReference_fields on NewReference {
    id
    title {
      id
      de
      en
      fr
      it
    }
    description {
      id
      de
      en
      fr
      it
    }
    projectId
  }
`;

export const employeeReferenceFragment: TypedDocumentNode<NewReference> = gql`
  fragment EmployeeReference_fields on NewReference {
    id
    title {
      id
      de
      en
      fr
      it
    }
    description {
      id
      de
      en
      fr
      it
    }
    project {
      id
      name {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const EDIT_REFERENCE_QUERY: TypedDocumentNode<
  EditReferenceQuery,
  EditReferenceQueryVariables
> = gql`
  ${newReferenceFragment}

  query EditReference($id: ID!) {
    reference(id: $id) {
      ...NewReference_fields
      approvedEntityId
      project {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
      employee {
        id
        firstName
        lastName
      }
    }
  }
`;

export const CREATE_REFERENCE_MUTATION: TypedDocumentNode<
  CreateNewReferenceMutationMutation,
  CreateNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation CreateNewReferenceMutation($fields: InputNewReference!) {
    createNewReference(fields: $fields) {
      ...NewReference_fields
    }
  }
`;

export const EDIT_REFERENCE_MUTATION: TypedDocumentNode<
  EditNewReferenceMutationMutation,
  EditNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation EditNewReferenceMutation($id: ID!, $fields: InputNewReference!) {
    editNewReference(id: $id, fields: $fields) {
      ...NewReference_fields
      draft {
        ...NewReference_fields
      }
    }
  }
`;

export const EDIT_AND_APPROVE_REFERENCE_MUTATION: TypedDocumentNode<
  EditAndApproveNewReferenceMutationMutation,
  EditAndApproveNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation EditAndApproveNewReferenceMutation(
    $id: ID!
    $fields: InputNewReference!
  ) {
    editAndApproveNewReference(id: $id, fields: $fields) {
      ...NewReference_fields
    }
  }
`;

export const DELETE_REFERENCE_MUTATION: TypedDocumentNode<
  DeleteNewReferenceMutationMutation,
  DeleteNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation DeleteNewReferenceMutation($id: ID!) {
    deleteNewReference(id: $id) {
      ...NewReference_fields
      draft {
        ...NewReference_fields
      }
    }
  }
`;

export const ONEPAGER_STARRED_REFERENCE_QUERY: TypedDocumentNode<
  StarredReferenceQuery,
  StarredReferenceQueryVariables
> = gql`
  query StarredReference {
    me {
      id
      starredReferences {
        id
        referenceId
      }
    }
  }
`;

export const ADD_STARRED_REFERENCE_MUTATION: TypedDocumentNode<
  AddStarredNewReferenceMutationMutation,
  AddStarredNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation AddStarredNewReferenceMutation($id: ID!) {
    addStarredReference(id: $id) {
      id
      reference {
        ...NewReference_fields
        isStarredForCurrentUser
      }
    }
  }
`;

export const REMOVE_STARRED_REFERENCE_MUTATION: TypedDocumentNode<
  RemoveStarredNewReferenceMutationMutation,
  RemoveStarredNewReferenceMutationMutationVariables
> = gql`
  ${newReferenceFragment}

  mutation RemoveStarredNewReferenceMutation($id: ID!) {
    removeStarredReference(id: $id) {
      id
      reference {
        ...NewReference_fields
        isStarredForCurrentUser
      }
    }
  }
`;

export const ALL_PROJECT_REFERENCE_QUERY: TypedDocumentNode<
  AllProjectNewReferenceQueryQuery,
  AllProjectNewReferenceQueryQueryVariables
> = gql`
  query allProjectNewReferenceQuery {
    allProjectReferences {
      id
      title {
        id
        de
        en
        fr
        it
      }
      projectId
      project {
        id
        abacusProjectId
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
  }
`;

export const ONEPAGER_REFERENCE_QUERY: TypedDocumentNode<
  OnepagerReferenceQuery,
  OnepagerReferenceQueryVariables
> = gql`
  ${newReferenceShortFragment}

  query OnepagerReference($id: ID!) {
    reference(id: $id) {
      ...NewReference_shortFields
      childReferences {
        id
        employeeId
        employee {
          id
          fullName
          roles {
            id
            role {
              id
              name {
                id
                de
                en
                fr
                it
              }
            }
          }
        }
      }
    }
  }
`;

export const FILTERED_PROJECT_REFERENCES_QUERY: TypedDocumentNode<
  FilteredProjectReferencesQuery,
  FilteredProjectReferencesQueryVariables
> = gql`
  query FilteredProjectReferences($filter: ReferenceFilter!) {
    filteredProjectReferences(filter: $filter) {
      id
      title {
        id
        de
        en
        fr
        it
      }
      description {
        id
        de
        en
        fr
        it
      }
      applicationAreas
      services
      availableLanguages
      projectId
      project {
        id
        abacusProjectId
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
  }
`;
